import type { IFieldDefinition, IRecord } from '@platform/core/types';
import type { IPartyCalendarCell } from './IPartyCalendarCell';
import type { IPartyCalendarType } from './IPartyCalendarType';
import type { IPartyEvent } from './IPartyEvent';

/**
 * Обчислення календаря партії (прототип v4 pcal). Чисті функції: ключі дат типу, події, місяць з адреси, сітка 6 тижнів від понеділка,
 * найближчі події. Дати — локальні (YYYY-MM-DD), як у прототипі.
 */

/** Поля-дати, яким прототип віддає перевагу (dkey), у порядку перевірки; решта дат — після них у порядку визначення. */
const PREFERRED: readonly string[] = ['start', 'date', 'hour', 'vdate', 'sentOn', 'receivedOn', 'due'];
/** Клітинок сітки: 6 тижнів. */
const CELLS = 42;
/** Скільки найближчих подій показує бічна колонка (прототип: 12). */
export const UPCOMING_LIMIT = 12;
const MONTH = /^(\d{4})-(\d{2})$/;

const pad = (n: number) => String(n).padStart(2, '0');

/** Локальна дата YYYY-MM-DD (не UTC). */
export function localIso(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Ключі полів-дат типу: бажані (start, date, hour, vdate, sentOn, receivedOn, due), що є серед дат типу, далі решта дат у порядку полів. */
export function calendarDateKeys(fields: readonly IFieldDefinition[]): string[] {
  const dates = fields.filter((f) => f.type === 'date').map((f) => f.key);
  return [...PREFERRED.filter((k) => dates.includes(k)), ...dates.filter((k) => !PREFERRED.includes(k))];
}

/** Події: типи в їхньому порядку, записи кожного — за кодом; дата — перше заповнене поле з dateKeys (10 символів). Записи без дати пропускаються. */
export function partyEvents(types: readonly IPartyCalendarType[], records: Readonly<Record<string, IRecord[]>>): IPartyEvent[] {
  const out: IPartyEvent[] = [];
  for (const type of types) {
    for (const record of [...(records[type.key] ?? [])].sort((a, b) => a.code - b.code)) {
      const value = type.dateKeys.map((k) => record.values[k]).find((v) => typeof v === 'string' && v !== '');
      if (typeof value === 'string') out.push({ date: value.slice(0, 10), type, record });
    }
  }
  return out;
}

/** Місяць YYYY-MM з параметра адреси; порожній чи невідомого формату — місяць `today`. */
export function monthKeyOf(value: string | null | undefined, today: Date): string {
  const m = MONTH.exec(value ?? '');
  const month = m ? Number(m[2]) : 0;
  return m && month >= 1 && month <= 12 ? `${m[1]}-${m[2]}` : `${today.getFullYear()}-${pad(today.getMonth() + 1)}`;
}

/** Місяць, зсунутий на `n` (від’ємне — назад). */
export function shiftMonth(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y ?? 1970, (m ?? 1) - 1 + n, 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

/** Перший день місяця (локальна дата). */
export function monthStart(month: string): Date {
  const [y, m] = month.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, 1);
}

/** Сітка місяця: 42 дні від понеділка тижня, що містить перше число; події кожного дня — у порядку списку подій. */
export function monthCells(month: string, events: readonly IPartyEvent[], today: string): IPartyCalendarCell[] {
  const first = monthStart(month);
  const shift = (first.getDay() + 6) % 7;
  const byDate = new Map<string, IPartyEvent[]>();
  for (const e of events) byDate.set(e.date, [...(byDate.get(e.date) ?? []), e]);
  return Array.from({ length: CELLS }, (_, i) => {
    const d = new Date(first.getFullYear(), first.getMonth(), 1 - shift + i);
    const iso = localIso(d);
    return { iso, day: d.getDate(), inMonth: d.getMonth() === first.getMonth(), today: iso === today, events: byDate.get(iso) ?? [] };
  });
}

/** Скільки подій у місяці. */
export function monthEventCount(events: readonly IPartyEvent[], month: string): number {
  return events.filter((e) => e.date.slice(0, 7) === month).length;
}

/** Найближчі події: від сьогодні включно, за датою (рівні — у порядку списку), перші `limit`. */
export function upcomingEvents(events: readonly IPartyEvent[], today: string, limit = UPCOMING_LIMIT): IPartyEvent[] {
  return events.filter((e) => e.date >= today).sort((a, b) => a.date.localeCompare(b.date)).slice(0, limit);
}
